import type {
  CharterLoyaltyCabinClass,
  CharterLoyaltyPointMultiplierResult,
  CharterLoyaltyPointMultiplierUpdateItem,
} from '../charter.types';

export type LoyaltyRow = {
  id: string;
  flightInstanceId: string;
  depDate: string;
  depAirport: string;
  arrAirport: string;
} & Partial<Record<CharterLoyaltyCabinClass, number>>;

export const VALID_MULTIPLIERS = [1, 2, 3] as const;
type LoyaltyMultiplier = (typeof VALID_MULTIPLIERS)[number];

export function mergeSavedMultipliers(
  results: Array<CharterLoyaltyPointMultiplierResult>,
  savedItems: Array<CharterLoyaltyPointMultiplierUpdateItem>,
): Array<CharterLoyaltyPointMultiplierResult> {
  const savedById = new Map(savedItems.map((item) => [item.flightInstanceId, item]));
  return results.map((result) => {
    const saved = savedById.get(result.flightInstanceId);
    if (!saved) return result;

    const multipliers = new Map(result.multipliers.map((item) => [item.cabinClass, item]));
    saved.multipliers.forEach((item) => multipliers.set(item.cabinClass, item));
    return { ...result, multipliers: Array.from(multipliers.values()) };
  });
}

function toLoyaltyMultiplier(value: unknown): LoyaltyMultiplier {
  const num = Number(value);
  if (num === 1 || num === 2 || num === 3) {
    return num;
  }
  throw new RangeError(`Invalid loyalty point multiplier: ${String(value)}`);
}

export const mapResultsToRows = (results: Array<CharterLoyaltyPointMultiplierResult>): Array<LoyaltyRow> => {
  return results.map((result) => {
    const multipliersByCabin = result.multipliers.reduce<Partial<Record<CharterLoyaltyCabinClass, number>>>(
      (acc, item) => ({ ...acc, [item.cabinClass]: item.loyaltyPointMultiplier }),
      {},
    );

    return {
      id: result.flightInstanceId,
      flightInstanceId: result.flightInstanceId,
      depDate: result.depDate,
      depAirport: result.depAirport,
      arrAirport: result.arrAirport,
      ...multipliersByCabin,
    } as LoyaltyRow;
  });
};

export const buildUpdatePayload = (
  editedRows: Map<string, LoyaltyRow>,
  activeCabinClasses: Array<CharterLoyaltyCabinClass>,
  baseRowMap: Map<string, LoyaltyRow>,
): { items: Array<CharterLoyaltyPointMultiplierUpdateItem> } => ({
  items: Array.from(editedRows.values()).map((row) => ({
    flightInstanceId: row.flightInstanceId,
    multipliers: activeCabinClasses
      .filter(
        (cabinClass) =>
          row[cabinClass] != null && row[cabinClass] !== baseRowMap.get(row.flightInstanceId)?.[cabinClass],
      )
      .map((cabinClass) => ({
        cabinClass,
        loyaltyPointMultiplier: toLoyaltyMultiplier(row[cabinClass]),
      })),
  })),
});

export function updateDraft(
  current: Map<string, LoyaltyRow>,
  row: LoyaltyRow,
  original: LoyaltyRow | undefined,
  cabinClasses: Array<CharterLoyaltyCabinClass>,
): Map<string, LoyaltyRow> {
  const next = new Map(current);
  if (!original || cabinClasses.some((cabin) => row[cabin] !== original[cabin])) {
    next.set(row.flightInstanceId, row);
  } else {
    next.delete(row.flightInstanceId);
  }
  return next;
}
